<template>
  <div class="dashboard__button--wrap" :class="{'edit-mode': isEditMode}">
    <!-- category bulk control -->
    <span v-if="!isEditMode"
          :style="getDomainEllipsisWidth()"
          class="domain-name">{{ currentDomainName }}</span>

    <span class="domain-timezone" :class="{'edit-mode': isEditMode}">
<!--      {{$t('timezone.dashboard.info')}} : ({{domain.timezone}}) {{domain.timezoneName}}-->
      <button v-if="domain.updatable && !isEditMode && !isSharedDomain" @click="isOpenTimezone = true">
        <i class="icon icon--fix" ></i>
      </button>
    </span>

    <div v-if="bulkActions"
         class="bulk-control--wrap"
         :class="{disabled: !this.selectedApplies.length, 'edit-mode': isEditMode}"
         :disabled="!this.selectedApplies.length">
      <ul>
        <li
          v-for="(item, index) in bulkActions" :key="index"
          :class="{ disabled: item.isDisabled }"
          @click.stop="onBulkActionClick(item.name)">
          <i class="icon" :class="`icon--bulk-${item.name}`"></i>
          <span v-html="$t(`dashboard.editMode.groupTable.bulk.${item.name}.title`)"></span>
          <template v-if="item.name === 'startDate' || item.name === 'endDate'">
            <div style="position: relative;">
              <date-picker
                v-if="calendarType === item.name"
                :isShowTimezone="true"
                :isShowTimezoneValue="false"
                :minDate="item.minDate"
                :maxDate="item.maxDate"
                @input="$emit('onBulkDateChange', item.name, $event)"
                @onCloseClick="calendarType = null"
              >
                <div slot="calendarOptions" class="date_picker_option">
                  <p v-if="item.name === 'startDate'">
                    <ui-checkbox @input="$emit('onBulkDateChange', item.name, null), calendarType = null">
                      {{ $t('common.instantlyStart') }}
                    </ui-checkbox>
                  </p>
                  <p v-if="item.name === 'endDate'">
                    <ui-checkbox @input="$emit('onBulkDateChange', item.name, null), calendarType = null">
                      {{ $t('common.nonStop') }}
                    </ui-checkbox>
                  </p>
                </div>
              </date-picker>
            </div>
          </template>
        </li>
      </ul>
    </div>

    <!-- default-mode's button -->
    <div class="button--wrap-outer" :class="{'edit-mode': isEditMode}">
      <div v-if="isEditMode && searchKeyword && searchKeyword.length"
           class="input__search">
        <input
          type="text"
          class="search-input"
          :value="searchKeyword"
          :disabled="true">
        <i class="icon icon--search"></i>
      </div>
      <ul v-if="!isEditMode"
          class="button--wrap">
        <li @click.stop>
          <div class="input__search">
            <input
              id="dashboardSearchInput"
              class="search-input"
              :class="{open: showSearchForm}"
              type="text"
              @click="(showSearchForm = true)"
              :value="searchKeyword"
              @input="$emit('update:searchKeyword', $event.target.value.trim())"
            >
            <i class="icon icon--search"></i>
          </div>
        </li>
        <li v-if="userData.updatePermission">
          <ui-button
            iconName="icon--plus-white"
            :primary="true"
            :disabled="!canRegister"
            @click="isShowPageAdd = true">{{ $t('common.registerPage') }}</ui-button>
        </li>
        <li v-if="!isSharedDomain && userData.updatePermission">
          <ui-button key="edit"
            iconName="icon--pencil"
            :disabled="!hasApplies"
            @click="$emit('onEditModeClick')">{{ $t('common.edit') }}</ui-button>
        </li>
        <li v-if="!isSharedDomain">
          <ui-button key="trackingCode"
            iconName="icon--tracking-code"
            @click="$emit('onShowTrackingCodeClick')">{{ $t('common.trackingCode.title') }}</ui-button>
        </li>
        <li class="separator" v-if="userData.userNum === 'b170105e175055u968'">
          <span></span>
        </li>

        <li v-if="userData.userNum === 'b170105e175055u968'" @mouseenter="showSPAToolTip = true"
            @mouseleave="showSPAToolTip = false">
          <ui-switch
            :on="isSPA"
            :onValue="'SPA'"
            :offValue="'SPA'"
            :disabled="isSharedDomain || isABPlan"
            @click="onChangeSPA"
          />
          <transition name="fade">
            <ui-tooltip class="tooltip"
                        :arrowTop="true"
                        :arrowRight="true"
                        v-if="showSPAToolTip">
              <div v-html="isSPA ? $t('dashboard.spa.tooltip.spa') : $t('dashboard.spa.tooltip.mpa')"></div>
            </ui-tooltip>
          </transition>
        </li>
      </ul>

      <ul class="button--wrap edit-mode" v-else>
        <li>
          <ui-button key="cancel"
            iconName="icon--modify-cancel"
            size="large"
            @click="onCancelClick">{{ $t('common.cancel') }}</ui-button>
        </li>
        <li>
          <ui-button key="save"
                     iconName="icon--modify-save"
                     size="large"
                     :primary="true"
                     @click="onSaveClick">{{ $t('common.save') }}</ui-button>
        </li>
      </ul>
    </div>

    <dimmed v-if="isShowPageAdd">
      <content-box class="page_add" :width="'660px'">
        <button class="close" @click="isShowPageAdd = false">
          <i class="icon icon--close14"></i>
        </button>
        <h3 v-html="$t('dashboard.pageAdd.title')"></h3>
        <p class="desc" v-html="$t('dashboard.pageAdd.desc')"></p>
        <div class="site_code">
          <div class="site_code--wrap">
            <span v-if="userData.originalSiteCodeList.includes('ALL')">
              ALL
            </span>
            <span v-else>
            {{ userData.originalSiteCodeList.join(', ') }}
            </span>
          </div>
        </div>
        <div class="select">
          <div class="half">
            <p class="title">
              <span v-html="$t('dashboard.pageAdd.withoutCountryCode')"></span>
            </p>
            <ui-button
              :primary2="true"
              @click="$router.push({ name: 'page-add-etc' })">{{ $t('common.register') }}</ui-button>
          </div>
          <div class="half">
            <p class="title">
              <span v-html="$t('dashboard.pageAdd.withCountryCode')"></span>
            </p>
            <ui-button
              :primary2="true"
              @click="$router.push({ name: 'page-add' })">{{ $t('common.register') }}</ui-button>
          </div>
        </div>
      </content-box>
    </dimmed>

    <timezone-setting
      v-if="isOpenTimezone"
      @closePopup="isOpenTimezone = false"
      @confirm="onSelectTimezone"
      :isClose="true"
      :domain="domain.rootDomain"
      :timezone="domain.timezone"
      :timezoneName="domain.timezoneName"
    ></timezone-setting>

    <timezone-no-edit
      v-if="!isTimezoneEdit"
      @check="onChangeTimezone"
      :timezoneName="domain.timezoneName"
      :domain="timezoneDomain"></timezone-no-edit>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import DatePicker from '@/components/date-picker'
import AppDialog from '@/components/ui/AppDialog'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiButton from '@/components/ui/UiButton'
import UiSwitch from '@/components/ui/UiSwitch.vue'
import UiTooltip from '@/components/ui/UiTooltip.vue'
import ContentBox from '@/components/ui/ContentBox'
import Dimmed from '@/components/ui/Dimmed'
import { create } from 'vue-modal-dialogs'
import { parse, addDays, startOfDay } from 'date-fns'
import timezones from 'google-timezones-json'
import TimezoneSetting from '@/components/page-registration/TimezoneSetting.vue'
import TimezoneNoEdit from '@/components/page-registration/TimezoneNoEdit.vue'
import { updateTimezone, validateTimezone } from '@/api/timezone'

const openDialog = create(AppDialog, 'dialogType')
const openTimezone = create(AppDialog, 'customData', 'contentStyle')

function getTsList (dateList) {
  return dateList.filter(item => !!item).map(item => parse(item).getTime())
}

function getMaxDate (items) {
  console.log(items)
  const tsList = getTsList(items)
  if (!tsList.length) {
    return null
  }
  return parse(Math.max(...tsList))
}

function getMinDate (items) {
  const tsList = getTsList(items)
  if (!tsList.length) {
    return null
  }
  return parse(Math.min(...tsList))
}

export default {
  name: 'DashboardAction',
  components: {
    DatePicker,
    UiCheckbox,
    UiButton,
    ContentBox,
    Dimmed,
    UiSwitch,
    UiTooltip,
    TimezoneSetting,
    TimezoneNoEdit
  },
  props: [
    'canRegister',
    'isEditMode',
    'selectedApplies',
    'currentDomainName',
    'searchKeyword',
    'hasApplies',
    'isSharedDomain',
    'isSPA',
    'domain'
  ],
  data () {
    return {
      calendarType: null,
      selectedDateValue: null,
      showSearchForm: false,
      showSPAToolTip: false,
      isOpenTimezone: false,
      isTimezoneEdit: true,
      timezoneDomain: '',
      isShowPageAdd: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'isABPlan'
    ]),
    items () {
      return Object.entries(timezones).map(([key, value]) => {
        return {
          value: key,
          text: `(${value.substr(4, 6)}) ${key}`
        }
      }).sort((a, b) => {
        return a.value.localeCompare(b.value)
      })
    },
    test () {
      return 100
    },
    bulkActions () {
      if (!this.isEditMode) {
        return null
      }

      let disableStartDate = false
      let disableEndDate = false

      for (const item of this.selectedApplies) {
        if (!item.meta.canChangeStartDate) {
          disableStartDate = true
        }

        if (!item.meta.canChangeEndDate) {
          disableEndDate = true
        }
      }

      const today = startOfDay(new Date())
      const tomorrow = addDays(today, 1)

      let maxStartDate = null
      let minEndDate = null

      if (!disableStartDate) {
        const endDateList = this.selectedApplies.map(item => item.endDate)
        maxStartDate = getMinDate(endDateList)
      }

      if (!disableEndDate) {
        const startDateList = this.selectedApplies.map(item => item.startDate)
        minEndDate = getMaxDate(startDateList) || parse(today)
      }

      if (maxStartDate && startOfDay(maxStartDate) < tomorrow) {
        disableStartDate = true
      }

      return [
        {
          name: 'move'
        },
        {
          name: 'delete'
        },
        {
          name: 'startDate',
          isDisabled: disableStartDate,
          minDate: tomorrow,
          maxDate: maxStartDate
        },
        {
          name: 'endDate',
          isDisabled: disableEndDate,
          minDate: minEndDate,
          maxDate: null
        }
      ]
    }
  },
  mounted () {
    this.handler = () => {
      if (!this.searchKeyword && this.showSearchForm) {
        this.showSearchForm = false
      }
    }
    document.body.addEventListener('click', this.handler, false)
  },
  destroyed () {
    document.body.removeEventListener('click', this.handler)
  },
  methods: {
    onChangeTimezone () {
      this.$emit('reload')
      this.isTimezoneEdit = true
    },
    openTimezoneSubDomainConfirm (result) {
      const params = {
        domain: result.rootDomain,
        timezone: result.timezone,
        timezoneName: result.timezoneName,
        subDomain: this.timezoneSubDomains
      }
      return openTimezone({
        type: 'confirm',
        iconClass: 'icon--modification',
        title: this.$t('timezone.timezoneChange.title'),
        description: this.$t('timezone.timezoneChange.desc'),
        data: params,
        from: 'timezone'
      }, {
        padding: '50px 30px 25px'
      }).then(result => {
        return result
      })
    },
    onUpdateTimezone (value) {
      const params = {
        domain: this.domain.domain,
        timezone: value.timezone,
        timezoneName: value.timezoneName,
        sid: this.domain.sid
      }
      updateTimezone(params).then(result => {
        this.domain.timezone = value.timezone
        this.$emit('reload')
        this.isOpenTimezone = false
      })
    },
    onSelectTimezone (value) {
      const domain = this.domain.domain

      validateTimezone({ domain: domain }).then(async result => {
        const isEdit = result.updatable
        const rootDomain = result.rootDomain
        this.timezoneDomain = rootDomain
        const subDomains = result.domains
        this.timezoneSubDomains = subDomains

        if (!isEdit) {
          this.isOpenTimezone = false
          this.isTimezoneEdit = false
        } else {
          if (this.timezoneSubDomains.length > 1) {
            this.isOpenTimezone = false
            const param = {
              rootDomain: rootDomain,
              timezone: value.timezone,
              timezoneName: value.timezoneName
            }
            const subDomainCheck = await this.openTimezoneSubDomainConfirm(param)
            if (subDomainCheck) {
              this.onUpdateTimezone(value)
            }
          } else {
            this.onUpdateTimezone(value)
          }
        }
      })
    },
    onChangeSPA () {
      const messageType = this.isSPA ? 'dashboard.spaOffChange' : 'dashboard.spaOnChange'
      openDialog(messageType).then(result => {
        if (result) {
          this.$emit('onChangeSPA')
        }
      })
    },
    getDomainEllipsisWidth () {
      let exceptElement = document.querySelector('.button--wrap-outer .button--wrap') ? document.querySelector('.button--wrap-outer .button--wrap') : 0
      let exceptElementWidth = exceptElement ? exceptElement.clientWidth : 0
      let exceptWidth = Number(exceptElementWidth) + 20

      let ellipsisWidth = `calc(100% - ${exceptWidth}px)`

      return {
        width: ellipsisWidth
      }
    },
    onBulkActionClick (actionType) {
      switch (actionType) {
        case 'move':
          this.$emit('onShowGroupMoveLayerClick')
          break
        case 'delete':
          openDialog('dashboard.deleteApply').then(result => {
            if (result) {
              this.$emit('onBulkDelete')
            }
          })
          break
        case 'startDate':
        case 'endDate':
          if (this.calendarType === actionType) {
            this.calendarType = null
          } else {
            this.calendarType = actionType
          }
          break
      }
    },
    onCancelClick () {
      openDialog('dashboard.editCancel').then(result => {
        result && this.$emit('onEditCancelClick')
      })
    },
    onSaveClick () {
      openDialog('dashboard.saveApplies').then(result => {
        if (result) {
          this.$emit('onSaveClick')
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard__button--wrap {
  position: relative;
  margin-top: 46px;
  padding-bottom: 22px;

  .input__search {
    position: relative;
    .search-input {
      width: 38px;
      height: 38px;
      line-height: 36px;
      border-radius: 20px;
      border: solid 1px #bbbbbb;
      background-color: #ffffff;
      box-sizing: border-box;
      font-size: 14px;
      color: #2f2f2f;

      transition: all 0.3s ease;

      cursor: pointer;

      &.open {
        width: 240px;
        padding-left: 18px;
        padding-right: 26px;

        cursor: text;
      }

      &:focus {
        border: solid 1px #289eef;
      }
    }

    .icon {
      position: absolute;
      right: 11px;
      transform: translateY(-50%);
      top: 50%;
    }
  }

  &.edit-mode {
    margin-top: 30px;
    padding-bottom: 17px;
    height: 63px;
    box-sizing: border-box;
    text-align: right;

    .input__search {
      float: left;
      margin-right: 16px;

      .icon {
        opacity: 0.3;
      }
    }

    .button--wrap-outer {
      .search-input {
        border: 1px solid #d7d7d7;
        background-color: #e6e6e6;
        color: rgba(47, 47, 47, 0.3)
      }
      .button--wrap {
        float: left;
        position: relative;

        &:before {
          content: '';
          display: inline-block;
          top: 50%;
          left: -6px;
          width: 1px;
          height: 20px;
          border-left: 1px solid #d7d7d7;
          position: absolute;
          transform: translateY(-50%);
        }
      }
    }
  }
  .domain-name {
    display: inline-block;
    font-size: 26px;
    font-weight: bold;
    color: #444444;
    line-height: 32px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-left: 10px;
  }

  .domain-timezone {
    position: absolute;
    top: -26px;
    left: 10px;
    font-size: 13px;
    color: #666;

    & button {
      width: 16px;
      height: 16px;
      vertical-align: middle;
      margin-left: 5px;
      top: 50%;
      transform: translate(0, -50%);
      position: absolute;

      &:hover {
        width: 24px;
        height: 24px;
        border-radius: 12px;
        background-color: rgba(0, 0, 0, 0.06);
        background-position: center;
        margin-left: 1px;
      }
      .icon--fix {
        vertical-align: middle;
        margin-top: -1px;
      }
    }

    &.edit-mode {
      position: static;
      float: left;
      margin-left: 30px;
      margin-top: 12px;
      font-size: 14px;
    }
  }

  .button--wrap-outer {
    position: absolute;
    top: calc(50% - 11px);
    right: 0;
    transform: translateY(-50%);

    &.edit-mode {
      position: relative;
      top:0;
      transform: translateY(0);
      display: inline-block;
      vertical-align: middle;
    }

    li {
      position: relative;
      float: left;

      .new {
        position: absolute;
        top: -8px;
        left: 18px;

        width: 32px;
        height: 16px;
        background: url(../../assets/img/icon-tag-new.svg);

        z-index: 1;
      }

      button {
        margin-left: 5px;

        &.ui-switch {
          width: 55px;
          margin-top: 6.5px;
        }
      }

      .tooltip {
        position: absolute;
        top: 42px;
        right: 8px;
        z-index: 10;
        text-align: right;
        font-size: 13px;
        line-height: 1.23;
        padding: 17px 20px;

        .arrow.bottom.right {
          right: 24px;
        }

        .text {
          color: #666666 !important;
          text-align: right;
        }
      }

      &.separator {
        span {
          width: 1px;
          height: 11px;
          background-color: #c1c1c1;
          display: inline-block;
          vertical-align: middle;
          margin: 13.5px 10px 13.5px 15px;
        }
      }
    }
  }

  .bulk-control--wrap {
    position: absolute;
    top: calc(50% - 11px);
    left: 0;
    transform: translateY(-50%);
    display: inline-block;
    z-index: 1;
    height: 38px;
    border-radius: 8px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #ffffff;
    font-size: 13px;
    color: #444444;
    padding: 4px;
    box-sizing: border-box;

    &.disabled {
      opacity: 0.2;
      pointer-events: none;
    }

    &.edit-mode {
      position: relative;
      top:0;
      transform: translateY(0);
      text-align: right;
      vertical-align: middle;
      margin-right: 18px;
    }

    & > ul {
      height: 100%;
      white-space: nowrap;

      & > li {
        position: relative;
        display: inline-block;
        height: 100%;
        padding-left: 34px;
        padding-right: 6px;
        cursor: pointer;
        margin-right: 13px;

        &.disabled {
          opacity: 0.3;
          pointer-events: none;
        }

        &.state {
          padding-right: 26px;
        }

        &:hover {
          @include hoverStyle;
          border-radius: 3px;
        }

        &:after {
          content: '';
          display: inline-block;
          width: 1px;
          height: 16px;
          border-right: 1px solid #d7d7d7;
          position: absolute;
          top: 50%;
          right: -7px;
          transform: translateY(-50%);
        }

        &:nth-last-of-type(1) {
          margin-right: 0;
          padding-right: 13px;
          &:after {
            content: none;
          }
        }

        .icon {
          position: absolute;
          top: 50%;
          left: 9px;
          transform: translateY(-50%);

          &.icon--toggle-slide16 {
            left: auto;
            right: 4px;
          }
        }
        & > span {
          display: inline-block;
          margin-left: 5px;
          line-height: 32px;
          font-weight: normal;
        }
      }
    }

    .state--wrap {
      position: absolute;
      bottom: -85px;
      left: 0;
      z-index: 1;
      .select__option--wrap {
        border-radius: 6px;
        box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
        background-color: #ffffff;
        overflow: hidden;
        .select__option {
          height: 38px;
          line-height: 38px;
          font-size: 14px;
          min-width: 108px;
          padding-left: 15px;
          padding-right: 15px;
          background-color: #fff;
          &:hover {
            background-color: #ebebeb;
          }
        }
      }
    }
  }
}

.page_add {
  color: #444444;

  .close {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  h3 {
    font-size: 20px;
    padding: 30px 30px 12px 30px;
    font-weight: bold;
  }

  .desc {
    font-size: 13px;
    padding: 0 30px;
  }

  .site_code {
    position: relative;
    margin: 30px;
    background: #f4f4f4;
    border: 1px solid #d7d7d7;
    border-radius: 5px;
    height: 167px;
    text-align: center;
    line-height: 2;
  }

  .site_code--wrap {
    @include positionXYcenter;
    width: calc(100% - 60px);
  }

  .select {
    padding: 0 30px 35px 30px;
    height: 141px;

    .half {
      float: left;
      width: calc(50% - 1px);
      height: 100%;
      text-align: center;
      border-left: 1px solid #d8d8d8;

      &:first-child {
        border-right: 1px solid #d8d8d8;
        border-left: none;
      }

      .title {
        position: relative;
        font-size: 20px;
        width: 290px;
        height: 65px;
        margin-bottom: 20px;
        font-weight: bold;
        line-height: 1.3;

        span {
          @include positionYcenter;
          display: block;
          width: 100%;
          text-align: center;
        }
      }

      .ui-btn {
        width: 156px;
      }
    }
  }
}

@media screen and (max-width: 1320px) {
  .dashboard__button--wrap {
    .domain-timezone {
      &.edit-mode {

      }

      & .ui_select {
        width: 125px !important;
      }
    }
  }
}

</style>
